/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useLocation } from "@epic-mw/app-context";
import { useLocale } from "@epic-mw/i18n";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import useSWR from "swr";

import { useAccountContext } from "../account/account.hooks";
import { useApiConfig } from "../api-config";

import { AuthRes, auth } from "./auth.fetch";
import { buildAuthRedirectUrl } from "./auth.utils";
import { resetSilentLoginAttempts, trackUserInteraction, trySilentLogin } from "./silent-login";

export const useAccountLoginRequired = (): { accountLoading: boolean; isLoggedIn?: boolean } => {
	const { account, isLoading } = useAccountContext();
	const { authPath } = useApiConfig();
	const router = useRouter();
	const location = useLocation();
	const locale = useLocale();
	const isLoggedIn = account.isLoggedIn;
	const redirected = useRef(false);

	useEffect(() => {
		if (redirected.current) return;
		if (account && !isLoading && !isLoggedIn) {
			redirected.current = true;
			console.info("useAccountLoginRequired() redirecting to /auth");
			router.push(buildAuthRedirectUrl({ authPath: authPath ?? "/auth", redirectUrl: location.toString(), locale }));
		}
	}, [account, authPath, isLoading, isLoggedIn, router, location, locale]);

	return { accountLoading: isLoading, isLoggedIn };
};

export const useAuth = (
	disable?: boolean,
): {
	auth?: AuthRes;
	isLoading: boolean;
	/** True while a fetch or revalidation of the session is in flight, including on focus. */
	isValidating: boolean;
	error?: string;
} => {
	const { refreshAccount, setAccount } = useAccountContext();
	const { serverUrl } = useApiConfig();
	/*
	  Revalidating on focus is a feature here, not SWR boilerplate. A user who
	  signs in on another epicgames.com tab and switches back must arrive signed
	  in, and the same applies to signing out. Nothing else notices: the session
	  cookie is written by the other tab, and this tab has no reason to re-read
	  it until something asks.

	  So this deliberately does NOT use `swr/immutable`, which sets
	  revalidateOnFocus, revalidateIfStale and revalidateOnReconnect to false. An
	  earlier commit on this branch switched to it, on the reasoning that the
	  local name `useSWRImmutable` should match its import. The name was wrong,
	  not the behaviour: `swr` has no named `useSWRImmutable` export, so that
	  import was plain `useSWR` all along, and "correcting" it silently removed
	  cross-tab sign-in detection. Named honestly now, with the options stated
	  rather than inherited, so the next reader cannot make the same trade.

	  Focus revalidation is throttled by SWR's `focusThrottleInterval`, 5s by
	  default. Left at the default on purpose: a longer window would swallow the
	  exact case this exists for, because signing in on another tab takes longer
	  than the throttle but far less than a minute.
	*/
	const { data, error, isValidating } = useSWR(!disable ? ["auth", serverUrl] : null, (_key, url) => auth(url), {
		errorRetryCount: 1,
		revalidateOnFocus: true,
		revalidateOnReconnect: true,
	});

	/*
	  Adopt whatever the auth call reports. Guard on `data` so this only fires
	  after a successful fetch, and not at all when `disable` is true, because
	  SWR is paused and `data` stays undefined.

	  `/api/cosmos/auth` returns the account it just verified, so take it
	  directly. That saves a round trip and, more importantly, keeps the client
	  from showing an account the server has already decided is signed out —
	  which is how the nav came to display a name on a page that rendered
	  logged out (MW-13701). Fall back to a fetch for responses without it.
	*/
	useEffect(() => {
		if (!data) return;
		if (data.account) {
			setAccount(data.account);
			return;
		}
		void refreshAccount();
	}, [data, refreshAccount, setAccount]);

	return {
		auth: data,
		isLoading: !data && !error,
		isValidating,
		error,
	};
};

/**
 * Take the silent sign-in the session check offered, for a user who is signed in to Epic on another
 * domain but not here.
 *
 * WebSSO sessions are per domain, and the session check is a `fetch`, which the auth worker never
 * turns into a silent sign-in. So a tab left open here stays signed out after the user signs in on
 * another Epic site. `/api/cosmos/auth` offers `silentLogin` only when Account Portal's SSO cookie
 * says they did; this follows it (see `trySilentLogin` for every guard).
 *
 * Takes `useAuth`'s result — `useSilentLogin(useAuth(disable))` — and deliberately stays separate
 * from it, so `useAuth` remains a data hook with no navigation side effect for its many callers.
 * Mounted where the session check runs for a whole page: `AccountAuthSync` (sites with
 * `layout.accountAuthSync`) and `next-templates/src/ClientPageProviders.tsx` (the hero-and-sections
 * template, on every site). Templates that call `useAuth` directly do not follow offers yet; rolling
 * it out to them is a follow-up. Mounting it twice on one page is safe: the first attempt records
 * the fingerprint before navigating, so the second declines.
 *
 * Attempts only when a fetch or revalidation has just completed, never from cached data. The
 * cache can be stale — the user may have signed in on this very site in another tab — and a
 * revalidation that returns the same answer keeps the same `auth` reference, so `isValidating`
 * settling is the signal. SWR revalidates on focus and visibility, which covers a tab that was
 * hidden when the offer first arrived.
 *
 * A settled answer that says signed in resets the attempt record, so after a sign-in — and so also
 * after the sign-out that follows it — the tab starts again with a full attempt budget, while the
 * SSO cookies of the session it just had are never followed back in.
 */
export const useSilentLogin = ({
	auth,
	isValidating,
}: Pick<ReturnType<typeof useAuth>, "auth" | "isValidating">): void => {
	useEffect(() => {
		trackUserInteraction();
	}, []);

	useEffect(() => {
		if (isValidating || !auth) return;
		// A confirmed sign-in resets the attempt record; see `resetSilentLoginAttempts`.
		if (auth.account?.isLoggedIn) {
			resetSilentLoginAttempts(auth.ssoFingerprint);
			return;
		}
		if (auth.silentLogin) trySilentLogin(auth.silentLogin);
	}, [auth, isValidating]);
};
